import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Code2, ShieldCheck, ShieldX, Clock, AlertTriangle, ChevronDown, ChevronUp, X
} from "lucide-react";
import { authFetch, API } from "../utils/api";

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function WidgetCard({ w, onApprove, onReject, acting }) {
  const [expanded, setExpanded] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const isActing = acting === w.id;

  const handleReject = () => {
    onReject(w.id, rejectReason);
    setRejectOpen(false);
    setRejectReason("");
  };

  return (
    <div
      data-testid={`widget-card-${w.id}`}
      className="bg-[#1a1a1a] border border-white/[0.08] rounded-2xl overflow-hidden"
    >
      {/* Card header */}
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
          <Code2 size={16} className="text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-bold text-white truncate">{w.name || "Script sin nombre"}</span>
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 flex-shrink-0">
              PENDIENTE
            </span>
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <Clock size={10} /> {fmt(w.submitted_at)}
            {w.position && <><span className="text-gray-700">·</span> posición: <code className="text-gray-400 text-[10px]">{w.position}</code></>}
          </p>
          {w.reason && (
            <p className="text-xs text-amber-400/80 mt-1.5 flex items-center gap-1.5">
              <AlertTriangle size={10} className="flex-shrink-0" /> {w.reason}
            </p>
          )}
        </div>
      </div>

      {/* Code toggle */}
      <button
        data-testid={`widget-code-toggle-${w.id}`}
        onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-2 flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 border-t border-white/[0.05] transition-colors bg-white/[0.02]"
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {expanded ? "Ocultar código" : "Ver código del script"}
      </button>

      {expanded && (
        <pre
          data-testid={`widget-code-${w.id}`}
          className="px-4 py-3 bg-black/40 text-[11px] text-emerald-300 font-mono overflow-x-auto max-h-48 border-t border-white/[0.05]"
        >{w.code}</pre>
      )}

      {/* Reject reason input */}
      {rejectOpen && (
        <div className="px-4 py-3 border-t border-white/[0.05] bg-red-950/20 flex gap-2 items-center">
          <input
            data-testid={`reject-reason-${w.id}`}
            type="text"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Motivo del rechazo (opcional)"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-red-500/50"
          />
          <button
            data-testid={`confirm-reject-${w.id}`}
            onClick={handleReject}
            disabled={isActing}
            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50"
          >
            {isActing ? "..." : "Confirmar"}
          </button>
          <button
            onClick={() => { setRejectOpen(false); setRejectReason(""); }}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={14} className="text-gray-400" />
          </button>
        </div>
      )}

      {/* Action buttons */}
      {!rejectOpen && (
        <div className="px-4 py-3 border-t border-white/[0.05] flex gap-2">
          <button
            data-testid={`approve-widget-${w.id}`}
            onClick={() => onApprove(w.id)}
            disabled={isActing}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs font-bold rounded-xl transition-colors disabled:opacity-50"
          >
            <ShieldCheck size={13} />
            {isActing ? "Procesando..." : "Aprobar e inyectar"}
          </button>
          <button
            data-testid={`reject-widget-${w.id}`}
            onClick={() => setRejectOpen(true)}
            disabled={isActing}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs font-bold rounded-xl border border-red-500/20 transition-colors disabled:opacity-50"
          >
            <ShieldX size={13} /> Rechazar
          </button>
        </div>
      )}
    </div>
  );
}

export function WidgetsSection({ onRefresh }) {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/widgets/pending`);
      if (res?.ok) {
        const d = await res.json();
        setWidgets(d.widgets || []);
      }
    } catch { toast.error("Error al cargar widgets"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setActing(id);
    try {
      const res = await authFetch(`${API}/admin/widgets/${id}/approve`, { method: "POST" });
      if (res?.ok) {
        toast.success("Widget aprobado e inyectado en el sitio");
        setWidgets(w => w.filter(x => x.id !== id));
        onRefresh();
      } else toast.error("Error al aprobar");
    } catch { toast.error("Error de conexión"); }
    finally { setActing(null); }
  };

  const reject = async (id, reason) => {
    setActing(id);
    try {
      const res = await authFetch(`${API}/admin/widgets/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: reason || "No aprobado por el administrador" }),
      });
      if (res?.ok) {
        toast.success("Widget rechazado");
        setWidgets(w => w.filter(x => x.id !== id));
        onRefresh();
      } else toast.error("Error al rechazar");
    } catch { toast.error("Error de conexión"); }
    finally { setActing(null); }
  };

  if (loading) return <div className="text-center py-16 text-gray-500 text-sm">Cargando widgets...</div>;

  if (widgets.length === 0) return (
    <div className="flex flex-col items-center justify-center py-20 px-6">
      <ShieldCheck size={48} className="text-emerald-500/60 mb-4" />
      <p className="text-white font-bold text-base mb-1">Sin widgets pendientes</p>
      <p className="text-gray-500 text-sm text-center">Todos los scripts de clientes han sido revisados.</p>
    </div>
  );

  return (
    <div className="px-4 pt-4 pb-8 flex flex-col gap-3">
      {widgets.map(w => (
        <WidgetCard key={w.id} w={w} onApprove={approve} onReject={reject} acting={acting} />
      ))}
    </div>
  );
}
